import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { ArrowFullDownIcon } from '@motorway/mw-highway-code';

import { COMMON_COMPONENTS_EVENTS } from 'Utilities/analytics/analyticsComponents';

import styles from './Expandable.scss';

export interface ExpandableProps {
  caption: string;
  children: React.ReactNode;
  className?: string;
  gaEvent?: {
    commonQuestionClicked: (arg: string) => void;
  };
  id: string;
  isForceExpanded?: boolean;
  snowplowEventLabel?: string;
}

export const Expandable = ({
  caption,
  children,
  className,
  gaEvent,
  id,
  isForceExpanded,
  snowplowEventLabel,
}: ExpandableProps) => {
  const [expanded, setExpanded] = useState(false);
  const [height, setHeight] = useState(isForceExpanded ? 'min-content' : '0');

  const content = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    gaEvent?.commonQuestionClicked?.(expanded ? 'Closed' : 'Opened');

    if (snowplowEventLabel) {
      COMMON_COMPONENTS_EVENTS.EXPANDABLE_CLICK(`${snowplowEventLabel} ${expanded ? 'Closed' : 'Opened'}`);
    }

    setExpanded(!expanded);
  };

  useEffect(() => {
    if (!isForceExpanded) {
      setHeight(expanded ? `${content?.current?.scrollHeight}px` : '0');
    }
  }, [isForceExpanded, expanded]);

  return (
    <div className={cx(
      styles.component,
      className,
      { [styles.expanded]: expanded || isForceExpanded },
      { [styles.forceExpanded]: isForceExpanded },
    )}>
      <h3>
        <button
          aria-controls={`expandable-panel-${id}`}
          aria-expanded={expanded}
          className={styles.caption}
          data-testid="expandable-button"
          id={`expandable-header-${id}`}
          onClick={(e) => {
            e.preventDefault();
            handleClick();
          }}
          tabIndex={0}
          type="button"
        >
          <span className={styles.text}>{caption}</span>
          {!isForceExpanded && (<span className={styles.icon}>
            <ArrowFullDownIcon />
          </span>)}
        </button>
      </h3>
      <div
        aria-hidden={!expanded}
        aria-labelledby={`expandable-header-${id}`}
        className={cx(styles.contentWrapper)}
        data-testid="expandable-panel"
        id={`expandable-panel-${id}`}
        style={{ maxHeight: `${height}` }}
        tabIndex={-1}
      >
        <div ref={content} className={cx(styles.content)}>
          {children}
        </div>
      </div>
    </div>
  );
};
